import * as L from 'leaflet';
import * as turf from '@turf/turf';
import { FieldMapShape } from './field-map-shape';
import { ShapeType, StyleType } from './shape.interface';

const styleConfigs: Record<StyleType, Record<ShapeType, L.PathOptions>> = {
    DEFAULT: {
        FIELD: { color: '#3288d3', weight: 2, fillColor: '#3288d3', fillOpacity: 0, dashArray: '0, 0' },
        BLOCK: { color: '#d0475c', weight: 2, fillColor: '#d0475c', fillOpacity: 0, dashArray: '0, 0' },
        STUDY: { color: '#d9d9d9', weight: 2, fillColor: '#d9d9d9', fillOpacity: 0, dashArray: '5, 10' },
        PLOT: { color: '#d9d9d9', weight: 2, fillColor: '#d9d9d9', fillOpacity: 0, dashArray: '0, 0' },
    },
    HOVER: {
        FIELD: { color: '#3288d3', weight: 6, fillColor: '#3288d3', fillOpacity: 0, dashArray: '5, 10' },
        BLOCK: { color: '#d0475c', weight: 6, fillColor: '#d0475c', fillOpacity: 0, dashArray: '5, 10' },
        STUDY: { color: '#bd35d0', weight: 6, fillColor: '#bd35d0', fillOpacity: 0, dashArray: '5, 10' },
        PLOT: { color: '#d9d9d9', weight: 2, fillColor: '#d9d9d9', fillOpacity: 0.5, dashArray: '5, 10' },
    },
    SELECTED: {
        FIELD: { color: '#3288d3', weight: 6, fillColor: '#3288d3', fillOpacity: 0, dashArray: '0, 0' },
        BLOCK: { color: '#d0475c', weight: 6, fillColor: '#d0475c', fillOpacity: 0, dashArray: '0, 0' },
        STUDY: { color: '#bd35d0', weight: 6, fillColor: '#bd35d0', fillOpacity: 0.4, dashArray: '0, 0' },
        PLOT: { color: '#d9d9d9', weight: 6, fillColor: '#d9d9d9', fillOpacity: 0, dashArray: '0, 0' },
    },
    SELECTED_HOVER: {
        FIELD: { color: '#3288d3', weight: 6, fillColor: '#3288d3', fillOpacity: 0, dashArray: '0, 0' },
        BLOCK: { color: '#d0475c', weight: 6, fillColor: '#d0475c', fillOpacity: 0, dashArray: '0, 0' },
        STUDY: { color: '#bd35d0', weight: 6, fillColor: '#bd35d0', fillOpacity: 0.4, dashArray: '0, 0' },
        PLOT: { color: '#d9d9d9', weight: 6, fillColor: '#d9d9d9', fillOpacity: 0, dashArray: '0, 0' },
    },
    EDIT: {
        FIELD: { color: '#fdc648', weight: 4, fillColor: '#fdc648', fillOpacity: 0.5, dashArray: '0, 0' },
        BLOCK: { color: '#fdc648', weight: 4, fillColor: '#fdc648', fillOpacity: 0.5, dashArray: '0, 0' },
        STUDY: { color: '#fdc648', weight: 4, fillColor: '#fdc648', fillOpacity: 0.5, dashArray: '0, 0' },
        PLOT: { color: '#fdc648', weight: 4, fillColor: '#fdc648', fillOpacity: 0.5, dashArray: '0, 0' },
    },
    BOUNDARY: {
        FIELD: { color: '#ffffff', weight: 4, fillColor: '#ffffff', fillOpacity: 0.5, dashArray: '5, 5' },
        BLOCK: { color: '#ffffff', weight: 4, fillColor: '#ffffff', fillOpacity: 0.5, dashArray: '5, 5' },
        STUDY: { color: '#ffffff', weight: 4, fillColor: '#ffffff', fillOpacity: 0.5, dashArray: '5, 5' },
        PLOT: { color: '#ffffff', weight: 4, fillColor: '#ffffff', fillOpacity: 0.5, dashArray: '5, 5' },
    },
    BOUNDARY_ERROR: {
        FIELD: { color: '#a50000', weight: 4, fillColor: '#a50000', fillOpacity: 0.5, dashArray: '5, 5' },
        BLOCK: { color: '#a50000', weight: 4, fillColor: '#a50000', fillOpacity: 0.5, dashArray: '5, 5' },
        STUDY: { color: '#a50000', weight: 4, fillColor: '#a50000', fillOpacity: 0.5, dashArray: '5, 5' },
        PLOT: { color: '#a50000', weight: 4, fillColor: '#a50000', fillOpacity: 0.5, dashArray: '5, 5' },
    },
};

export function updateStylePolygon(polygon: L.Polygon, type: ShapeType, style: StyleType) {
    const styleOptions = styleConfigs[style][type];

    if (!styleOptions) {
        console.warn(`No style configuration found for style: ${style} and type: ${type}`);
        return;
    }

    polygon.setStyle(styleOptions);
}

export function updateStyle(fieldMapShape: FieldMapShape, style: StyleType): void {
    const { polygon, type } = fieldMapShape;
    updateStylePolygon(polygon, type, style);
}

export function convertPolygonToTurf(polygon: L.Polygon) {
    const polygonCoords = (polygon.getLatLngs() as L.LatLng[][])[0].map((latlng) => [latlng.lng, latlng.lat]);

    // Ensure the polygon closes the loop
    if (polygonCoords[0] !== polygonCoords[polygonCoords.length - 1]) {
        polygonCoords.push(polygonCoords[0]);
    }

    return turf.polygon([polygonCoords], polygon.feature ? polygon.feature.properties : {});
}

export function convertTurfToPolygon(feature: turf.helpers.Feature<turf.helpers.Polygon, {}>): L.Polygon {
    // fetch first set of coordinates in a polygon
    const polygonCoords = feature.geometry.coordinates[0].map((coord: number[]) => new L.LatLng(coord[1], coord[0]));

    // Create the Leaflet polygon
    const polygon = L.polygon(polygonCoords);

    // Add properties to the polygon
    if (feature.properties) {
        polygon.feature = {
            type: 'Feature',
            properties: feature.properties,
            geometry: feature.geometry
        };
    }

    return polygon;
}

export function featureToL(feature) {
    return turf.getCoords(turf.flip(feature));
}

export function deepCloneLatLngs(latlngs: L.LatLng[] | L.LatLng[][] | L.LatLng[][][]): L.LatLng[] | L.LatLng[][] | L.LatLng[][][] {
    // @ts-ignore
    return latlngs.map((item) => {
        if (Array.isArray(item)) {
            // Recursively clone nested arrays
            return deepCloneLatLngs(item);
        } else if (item instanceof L.LatLng) {
            // Clone individual LatLng objects
            return new L.LatLng(item.lat, item.lng);
        }
        // If it's neither an array nor a LatLng, return as is (shouldn't happen in this context)
        return item;
    });
}

export function isPointInPolygon(point: L.LatLng, turfBoundingPolygon: any): boolean {
    const turfPoint = turf.point([point.lng, point.lat]);
    return turf.booleanPointInPolygon(turfPoint, turfBoundingPolygon);
}

export function convertGeoJSONStringToPolygon(geometry: string) {
    const geoJsonData = JSON.parse(geometry);

    // Extract coordinates from GeoJSON
    const coordinates = geoJsonData.geometry.coordinates[0];

    // Convert coordinates to LatLng array
    // Note: GeoJSON uses [longitude, latitude] while Leaflet uses [latitude, longitude]
    const latLngs = coordinates.map((coord: number[]) =>
        L.latLng(coord[1], coord[0])
    );

    // Create polygon
    const polygon = L.polygon(latLngs);
    return polygon;
}
